import { Fragment, useState } from "react";
import { INFORMATION_ACCORDIONS } from "../../information/helpers/accordionDeepLink";
import useInformationAccordionFocus from "../../information/hooks/useInformationAccordionFocus";
import { Accordion } from "react-bootstrap";
import { withNamespaces } from "react-i18next";
import { Button, ButtonGroup, Col, Row } from "reactstrap";
import DataFlowImage from "src/modules/data-protection/assets/images/workflow.svg";
import CreateDataFlowModal from "./components/CreateDataFlowModal";
import DataFlowPanel from "./components/DataFlowPanel";
import DataFlowVisualMap from "./components/DataFlowVisualMap";
import DeleteConfirmationModal from "./components/DeleteConfirmationModal";
import EditDataFlowOffcanvas from "./components/EditDataFlowOffcanvas";
import useDataFlowCards from "./hooks/useDataFlowCards";
import useDeleteDataFlow from "./hooks/useDeleteDataFlow";
import useWrapperAssetId from "./hooks/useWrapperAssetId";

const VIEW_LIST = "list";
const VIEW_MAP = "map";
const DATA_FLOW_ACCORDION_KEY = INFORMATION_ACCORDIONS.DATA_FLOW;

const DataFlowV2 = ({ t, assetId, wrapperAssetId: detailsAssetId, softwareName }) => {
    const [activeAccordion, setActiveAccordion] = useState("");

    useInformationAccordionFocus(DATA_FLOW_ACCORDION_KEY, setActiveAccordion);
    const [activeView, setActiveView] = useState(VIEW_LIST);
    const [cardToDelete, setCardToDelete] = useState(null);
    const [createDirection, setCreateDirection] = useState(null);
    const [cardToEdit, setCardToEdit] = useState(null);
    const { wrapperAssetId, isResolving } = useWrapperAssetId({
        softwareId: assetId,
        detailsAssetId,
        softwareName,
    });
    const {
        incomingCards,
        outgoingCards,
        methodLabels,
        typeLabels,
        categoryLabels,
        relationLabels,
        endpointTypeLabels,
        isLoading,
    } = useDataFlowCards({
        softwareId: assetId,
        wrapperAssetId,
        t,
    });
    const { deleteFlow, isDeleting } = useDeleteDataFlow({
        softwareId: assetId,
        t,
        onSuccess: () => setCardToDelete(null),
    });

    const handleAccordionClick = () => {
        setActiveAccordion((prev) =>
            prev === DATA_FLOW_ACCORDION_KEY ? null : DATA_FLOW_ACCORDION_KEY
        );
    };

    const handleCloseDeleteModal = () => {
        if (isDeleting) {
            return;
        }

        setCardToDelete(null);
    };

    const labelMaps = {
        methodLabels,
        typeLabels,
        categoryLabels,
        relationLabels,
        endpointTypeLabels,
    };

    return (
        <Fragment>
        <Accordion activeKey={activeAccordion} className="mt-4 data-flow-v2">
            <Accordion.Item
                eventKey={DATA_FLOW_ACCORDION_KEY}
                data-information-accordion={DATA_FLOW_ACCORDION_KEY}
                className={
                    activeAccordion === DATA_FLOW_ACCORDION_KEY ? "active" : ""
                }
            >
                <Accordion.Header onClick={handleAccordionClick}>
                    <img
                        src={DataFlowImage}
                        alt=""
                        className="data-flow-v2__header-icon me-2"
                    />
                    <span>{t("Data Flow")}</span>
                </Accordion.Header>

                <Accordion.Body>
                    <ButtonGroup className="data-flow-v2__view-toggle mb-3">
                        <Button
                            color="primary"
                            outline={activeView !== VIEW_LIST}
                            type="button"
                            onClick={() => setActiveView(VIEW_LIST)}
                        >
                            <i className="ri-list-check me-1" aria-hidden="true" />
                            {t("List View")}
                        </Button>
                        <Button
                            color="primary"
                            outline={activeView !== VIEW_MAP}
                            type="button"
                            onClick={() => setActiveView(VIEW_MAP)}
                        >
                            <i className="ri-share-line me-1" aria-hidden="true" />
                            {t("Visual Map")}
                        </Button>
                    </ButtonGroup>

                    <div
                        className={
                            activeView === VIEW_MAP ? "" : "d-none"
                        }
                    >
                        <DataFlowVisualMap
                            softwareName={softwareName}
                            incomingCards={incomingCards}
                            outgoingCards={outgoingCards}
                            methodLabels={methodLabels}
                            typeLabels={typeLabels}
                            relationLabels={relationLabels}
                            isLoading={isLoading}
                        />
                    </div>
                    {activeView === VIEW_MAP ? null : (
                        <Row className="g-3">
                            <Col xs={12} lg={6}>
                                <DataFlowPanel
                                    title={t("Receives Data From")}
                                    emptyText={t(
                                        "No incoming data flows added yet."
                                    )}
                                    cards={incomingCards}
                                    isLoading={isLoading}
                                    isDeleting={isDeleting}
                                    onAddClick={() => setCreateDirection("receives")}
                                    onEdit={setCardToEdit}
                                    onDelete={setCardToDelete}
                                    {...labelMaps}
                                />
                            </Col>
                            <Col xs={12} lg={6}>
                                <DataFlowPanel
                                    title={t("Sends Data To")}
                                    emptyText={t(
                                        "No outgoing data flows added yet."
                                    )}
                                    cards={outgoingCards}
                                    isLoading={isLoading}
                                    isDeleting={isDeleting}
                                    onAddClick={() => setCreateDirection("sends")}
                                    onEdit={setCardToEdit}
                                    onDelete={setCardToDelete}
                                    {...labelMaps}
                                />
                            </Col>
                        </Row>
                    )}
                </Accordion.Body>
            </Accordion.Item>

        </Accordion>

            <CreateDataFlowModal
                isOpen={Boolean(createDirection)}
                direction={createDirection}
                softwareId={assetId}
                wrapperAssetId={wrapperAssetId}
                isResolvingWrapper={isResolving}
                onClose={() => setCreateDirection(null)}
            />

            <EditDataFlowOffcanvas
                isOpen={Boolean(cardToEdit)}
                card={cardToEdit}
                softwareId={assetId}
                wrapperAssetId={wrapperAssetId}
                onClose={() => setCardToEdit(null)}
            />

            <DeleteConfirmationModal
                isOpen={Boolean(cardToDelete)}
                isDeleting={isDeleting}
                onClose={handleCloseDeleteModal}
                onConfirm={() => deleteFlow(cardToDelete?.id)}
            />
        </Fragment>
    );
};

export default withNamespaces()(DataFlowV2);
